import { z } from 'zod'

export type CodingCategoryId = 'event' | 'motion' | 'control' | 'loop' | 'sensor' | 'data'

export type CodingParameterOption = { label: string; value: string }

export type CodingParameterDefinition = {
  key: string
  label: string
  type: 'number' | 'select' | 'text'
  defaultValue: string | number
  min?: number
  max?: number
  step?: number
  unit?: string
  options?: CodingParameterOption[]
}

export type CodingBlockDefinition = {
  type: string
  category: CodingCategoryId
  title: string
  description: string
  parameters: CodingParameterDefinition[]
  container?: 'repeat' | 'forever' | 'condition'
}

export type CodingProgramBlock = {
  id: string
  type: string
  values: Record<string, string | number>
  children: CodingProgramBlock[]
}

export const DEFAULT_CODING_PROGRAM_NAME = '메인 프로그램'

export const CodingProgramBlockSchema: z.ZodType<CodingProgramBlock> = z.lazy(() => z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  values: z.record(z.string(), z.union([z.string(), z.number()])),
  children: z.array(CodingProgramBlockSchema),
}))

export const CODING_CATEGORIES: ReadonlyArray<{
  id: CodingCategoryId
  label: string
  symbol: string
}> = [
  { id: 'event', label: '시작', symbol: '⚑' },
  { id: 'motion', label: '움직임', symbol: '↻' },
  { id: 'control', label: '제어', symbol: '◇' },
  { id: 'loop', label: '반복', symbol: '⟳' },
  { id: 'sensor', label: '센서', symbol: '◉' },
  { id: 'data', label: '데이터', symbol: '=' },
]

const directionOptions: CodingParameterOption[] = [
  { label: '정방향', value: 'forward' },
  { label: '역방향', value: 'reverse' },
]

export const CODING_BLOCKS: readonly CodingBlockDefinition[] = [
  {
    type: 'when-start',
    category: 'event',
    title: '프로그램이 시작되면',
    description: '실행을 시작하는 첫 블록입니다.',
    parameters: [],
  },
  {
    type: 'motor-for',
    category: 'motion',
    title: '시간 동안 큐브 회전하기',
    description: '시간·큐브·속도·방향을 한 번에 지정합니다.',
    parameters: [
      { key: 'seconds', label: '시간', type: 'number', defaultValue: 1, min: 0.1, max: 60, step: 0.1, unit: '초' },
      { key: 'cube', label: '큐브', type: 'number', defaultValue: 1, min: 1, max: 12, step: 1 },
      { key: 'speed', label: '속도', type: 'number', defaultValue: 20, min: 0, max: 100, step: 5, unit: '%' },
      { key: 'direction', label: '방향', type: 'select', defaultValue: 'forward', options: directionOptions },
    ],
  },
  {
    type: 'motor-start',
    category: 'motion',
    title: '큐브 계속 회전하기',
    description: '정지 블록을 만날 때까지 큐브 모터를 회전합니다.',
    parameters: [
      { key: 'cube', label: '큐브', type: 'number', defaultValue: 1, min: 1, max: 12, step: 1 },
      { key: 'speed', label: '속도', type: 'number', defaultValue: 20, min: 0, max: 100, step: 5, unit: '%' },
      { key: 'direction', label: '방향', type: 'select', defaultValue: 'forward', options: directionOptions },
    ],
  },
  {
    type: 'motor-stop',
    category: 'motion',
    title: '큐브 모터 멈추기',
    description: '번호로 선택한 큐브의 모터만 정지합니다.',
    parameters: [
      { key: 'cube', label: '큐브', type: 'number', defaultValue: 1, min: 1, max: 12, step: 1 },
    ],
  },
  {
    type: 'stop-all',
    category: 'motion',
    title: '모든 큐브 멈추기',
    description: '회전 중인 모든 큐브 모터를 정지합니다.',
    parameters: [],
  },
  {
    type: 'wait',
    category: 'control',
    title: '기다리기',
    description: '다음 블록으로 넘어가기 전에 기다립니다.',
    parameters: [
      { key: 'seconds', label: '시간', type: 'number', defaultValue: 1, min: 0.1, max: 60, step: 0.1, unit: '초' },
    ],
  },
  {
    type: 'repeat',
    category: 'loop',
    title: '횟수만큼 반복하기',
    description: '안쪽에 넣은 블록을 지정한 횟수만큼 반복합니다.',
    parameters: [
      { key: 'count', label: '횟수', type: 'number', defaultValue: 3, min: 1, max: 100, step: 1, unit: '번' },
    ],
    container: 'repeat',
  },
  {
    type: 'forever',
    category: 'loop',
    title: '계속 반복하기',
    description: '안쪽 블록을 프로그램이 멈출 때까지 반복합니다.',
    parameters: [],
    container: 'forever',
  },
  {
    type: 'if-distance',
    category: 'control',
    title: '가까우면 실행하기',
    description: '거리 조건이 맞을 때만 안쪽 블록을 실행합니다.',
    parameters: [
      { key: 'sensor', label: '센서', type: 'number', defaultValue: 1, min: 1, max: 8, step: 1 },
      { key: 'distance', label: '거리', type: 'number', defaultValue: 10, min: 1, max: 100, step: 1, unit: 'cm' },
    ],
    container: 'condition',
  },
  {
    type: 'read-distance',
    category: 'sensor',
    title: '거리 값 읽기',
    description: '거리 센서의 현재 측정값을 읽습니다.',
    parameters: [
      { key: 'sensor', label: '센서', type: 'number', defaultValue: 1, min: 1, max: 8, step: 1 },
    ],
  },
  {
    type: 'button-state',
    category: 'sensor',
    title: '버튼 상태 확인하기',
    description: '선택한 큐브의 노란 버튼 상태를 확인합니다.',
    parameters: [
      { key: 'cube', label: '큐브', type: 'number', defaultValue: 1, min: 1, max: 12, step: 1 },
    ],
  },
  {
    type: 'set-variable',
    category: 'data',
    title: '변수 값 정하기',
    description: '이름과 숫자를 저장해 다른 블록에서 사용합니다.',
    parameters: [
      { key: 'name', label: '이름', type: 'text', defaultValue: '속도' },
      { key: 'value', label: '값', type: 'number', defaultValue: 20, min: -999, max: 999, step: 1 },
    ],
  },
  {
    type: 'change-variable',
    category: 'data',
    title: '변수 값 바꾸기',
    description: '저장된 숫자에 원하는 값을 더합니다.',
    parameters: [
      { key: 'name', label: '이름', type: 'text', defaultValue: '속도' },
      { key: 'amount', label: '변화', type: 'number', defaultValue: 5, min: -999, max: 999, step: 1 },
    ],
  },
]

export function codingBlockDefinition(type: string) {
  return CODING_BLOCKS.find((block) => block.type === type) ?? CODING_BLOCKS[0]
}

export function createCodingBlock(type: string, id: string): CodingProgramBlock {
  const definition = codingBlockDefinition(type)
  return {
    id,
    type,
    values: Object.fromEntries(definition.parameters.map((parameter) => [parameter.key, parameter.defaultValue])),
    children: [],
  }
}

export function createDefaultCodingProgram() {
  return [createCodingBlock('when-start', 'coding-start')]
}

export function normalizeCodingProgram(blocks: CodingProgramBlock[]): CodingProgramBlock[] {
  return blocks
    .filter((block) => block.type !== 'when-button')
    .map((block) => ({ ...block, children: normalizeCodingProgram(block.children) }))
}

export function allCodingBlocks(blocks: CodingProgramBlock[]): CodingProgramBlock[] {
  return blocks.flatMap((block) => [block, ...allCodingBlocks(block.children)])
}

export function codingCommandCount(blocks: CodingProgramBlock[]) {
  return allCodingBlocks(blocks).filter((block) => codingBlockDefinition(block.type).category !== 'event').length
}

export function codingProgramPreviewSequence(blocks: CodingProgramBlock[]) {
  const sequence: string[] = []
  const visit = (items: CodingProgramBlock[]) => items.forEach((block) => {
    sequence.push(block.id)
    const definition = codingBlockDefinition(block.type)
    const repeatCount = definition.container === 'repeat'
      ? Math.min(4, Math.max(1, Number(block.values.count) || 1))
      : 1
    for (let index = 0; index < repeatCount; index += 1) visit(block.children)
  })
  visit(blocks)
  return sequence
}

export function codingProgramEstimatedSeconds(blocks: CodingProgramBlock[]): number {
  return blocks.reduce((total, block) => {
    const definition = codingBlockDefinition(block.type)
    const ownSeconds = definition.category === 'event'
      ? 0
      : Number.isFinite(Number(block.values.seconds)) ? Number(block.values.seconds) : 0.35
    const childSeconds = codingProgramEstimatedSeconds(block.children)
    const multiplier = definition.container === 'repeat'
      ? Math.max(1, Number(block.values.count) || 1)
      : 1
    return total + ownSeconds + childSeconds * multiplier
  }, 0)
}

function quoted(value: string | number | undefined) {
  return JSON.stringify(value ?? '')
}

export function codingProgramToPython(blocks: CodingProgramBlock[]) {
  const lines = ['# PingPong Python 미리보기', 'from pingpong import Robot', '', 'robot = Robot()', '']
  function appendChildren(block: CodingProgramBlock, indent: number) {
    if (!block.children.length) lines.push(`${'    '.repeat(indent)}pass`)
    else block.children.forEach((child) => appendBlock(child, indent))
  }
  function appendBlock(block: CodingProgramBlock, indent = 0) {
    const prefix = '    '.repeat(indent)
    const value = block.values
    switch (block.type) {
      case 'when-start': lines.push(`${prefix}# 프로그램 시작`); break
      case 'motor-for': lines.push(`${prefix}robot.motor_for(cube=${value.cube}, speed=${value.speed}, direction=${quoted(value.direction)}, seconds=${value.seconds})`); break
      case 'motor-start': lines.push(`${prefix}robot.motor(cube=${value.cube}, speed=${value.speed}, direction=${quoted(value.direction)})`); break
      case 'motor-stop': lines.push(`${prefix}robot.stop(cube=${value.cube})`); break
      case 'stop-all': lines.push(`${prefix}robot.stop_all()`); break
      case 'wait': lines.push(`${prefix}robot.wait(${value.seconds})`); break
      case 'repeat':
        lines.push(`${prefix}for _ in range(${value.count}):`)
        appendChildren(block, indent + 1)
        break
      case 'forever':
        lines.push(`${prefix}while True:`)
        appendChildren(block, indent + 1)
        break
      case 'if-distance':
        lines.push(`${prefix}if robot.read_distance(sensor=${value.sensor}) < ${value.distance}:`)
        appendChildren(block, indent + 1)
        break
      case 'read-distance': lines.push(`${prefix}distance = robot.read_distance(sensor=${value.sensor})`); break
      case 'button-state': lines.push(`${prefix}pressed = robot.button_pressed(cube=${value.cube})`); break
      case 'set-variable': lines.push(`${prefix}${String(value.name || 'value')} = ${value.value}`); break
      case 'change-variable': lines.push(`${prefix}${String(value.name || 'value')} += ${value.amount}`); break
      default: lines.push(`${prefix}# ${codingBlockDefinition(block.type).title}`)
    }
  }
  blocks.forEach((block) => appendBlock(block))
  return lines.join('\n')
}
